// MIDI input → bytebeat pitch transposition.
// Listens for Web MIDI note events and adjusts the effective sample rate
// to transpose the currently-playing track in real time.
//
// Two transpose modes:
//   'speed'  — classic sample-rate shift: pitch + tempo change together
//   'pitch'  — resample to preserve tempo: computes f(t×ratio), skips/dupes

const STORAGE_ENABLED  = 'coderadio.midiEnabled';
const STORAGE_MODE     = 'coderadio.midiMode';
const STORAGE_SCALE    = 'coderadio.midiScale';

// Scale definitions — semitone offsets from root.
export const SCALES = {
	'Chromatic':       [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
	'Major':           [0, 2, 4, 5, 7, 9, 11],
	'Natural Minor':   [0, 2, 3, 5, 7, 8, 10],
	'Harmonic Minor':  [0, 2, 3, 5, 7, 8, 11],
	'Major Pentatonic':[0, 2, 4, 7, 9],
	'Minor Pentatonic':[0, 3, 5, 7, 10],
	'Blues':           [0, 3, 5, 6, 7, 10],
	'Dorian':          [0, 2, 3, 5, 7, 9, 10],
	'Phrygian':        [0, 1, 3, 5, 7, 8, 10],
	'Lydian':          [0, 2, 4, 6, 7, 9, 11],
	'Mixolydian':      [0, 2, 4, 5, 7, 9, 10],
};

/// Quantize a MIDI note number to the nearest note in the given scale.
/// Root note (0 = C) defaults to C4 = MIDI 60.
function quantizeToScale(note, scaleName, rootNote = 60) {
	const scale = SCALES[scaleName];
	if (!scale || scale.length === 0) return note;
	const rel = ((note - rootNote) % 12 + 12) % 12;
	// Find nearest scale degree
	let best = scale[0], bestDist = 12;
	for (const d of scale) {
		const dist = Math.min(Math.abs(rel - d), 12 - Math.abs(rel - d));
		if (dist < bestDist) { bestDist = dist; best = d; }
	}
	const octave = Math.floor((note - rootNote) / 12);
	return rootNote + octave * 12 + best;
}

export class MidiInput {
	constructor() {
		this.enabled = false;
		this.transposeMode = 'speed';   // 'speed' | 'pitch'
		this.scale = 'Chromatic';
		this._access = null;
		this._inputs = [];
		this._activeNotes = new Set();   // currently-held MIDI note numbers
		this._virtualNotes = new Set();  // notes from on-screen piano
		this._referenceSr = 8000;        // set by consumer (the track's sample rate)
		this._baseSr = 8000;
		this._ratio = 1.0;
		this._onChange = null;           // callback(ratio, noteCount)
		this._pitchWheel = 0;            // -8192..8191
	}

	/// Called when a new track loads with a given sample rate.
	setReferenceSampleRate(sr) {
		this._referenceSr = sr;
		this._recompute();
	}

	/// Start listening for MIDI events. Call after user gesture.
	async start() {
		if (!navigator.requestMIDIAccess) {
			console.warn('[midi] Web MIDI not available in this browser');
			return false;
		}
		try {
			this._access = await navigator.requestMIDIAccess();
			this._scanInputs();
			this._access.addEventListener('statechange', () => this._scanInputs());
			console.log('[midi] ready —', this._inputs.length, 'input(s)');
			return true;
		} catch (e) {
			console.warn('[midi] access denied:', e);
			return false;
		}
	}

	stop() {
		for (const inp of this._inputs) inp.close();
		this._inputs = [];
		this._activeNotes.clear();
		this._ratio = 1.0;
		if (this._onChange) this._onChange(1.0, 0);
	}

	/// Accept notes from the on-screen piano (or any virtual source).
	/// Merged with real MIDI notes in _recompute().
	setVirtualNotes(notes) {
		this._virtualNotes = new Set(notes);
		this._recompute();
	}

	/// Called by the audio worklet each buffer to apply the current ratio.
	get currentRatio() { return this._ratio; }

	/// For 'pitch' mode: the worklet needs to call f(t * ratio) instead of f(t).
	get rateMultiplier() {
		return this.transposeMode === 'speed' ? this._ratio : 1.0;
	}

	// ── Internals ─────────────────────────────────────────────────────

	_scanInputs() {
		// Close old inputs
		for (const inp of this._inputs) inp.close();
		this._inputs = [];
		if (!this._access) return;
		for (const entry of this._access.inputs.values()) {
			if (!entry.connection || entry.connection === 'closed') continue;
			const inp = entry;
			inp.addEventListener('midimessage', e => this._onMidi(e));
			this._inputs.push(inp);
			console.log('[midi] opened:', inp.name || entry.id);
		}
	}

	_onMidi(event) {
		if (!this.enabled) return;
		const [status, data1, data2] = event.data;
		const cmd = status & 0xf0;
		const channel = status & 0x0f;

		if (cmd === 0x90 && data2 > 0) {
			// Note On
			const quantized = quantizeToScale(data1, this.scale);
			this._activeNotes.add(quantized);
			this._recompute();
		} else if (cmd === 0x80 || (cmd === 0x90 && data2 === 0)) {
			// Note Off
			const quantized = quantizeToScale(data1, this.scale);
			this._activeNotes.delete(quantized);
			this._recompute();
		} else if (cmd === 0xe0) {
			// Pitch wheel
			this._pitchWheel = ((data2 << 7) | data1) - 8192;
			this._recompute();
		}
	}

	_recompute() {
		const allNotes = new Set([...this._activeNotes, ...this._virtualNotes]);
		if (allNotes.size === 0) {
			this._ratio = 1.0;
		} else {
			// Use the highest held note as the reference
			const notes = [...allNotes].sort((a, b) => a - b);
			const note = notes[notes.length - 1];
			// Base ratio: 2^(semitones/12)
			let ratio = Math.pow(2, (note - 60) / 12);
			// Pitch wheel: ±2 semitones
			if (this._pitchWheel !== 0) {
				ratio *= Math.pow(2, (this._pitchWheel / 8192) * 2 / 12);
			}
			this._ratio = Math.max(0.125, Math.min(8, ratio)); // clamp to ±3 octaves
		}
		if (this._onChange) this._onChange(this._ratio, this._activeNotes.size);
	}
}
